import React, { useState } from 'react';
import { X, Heart, CheckCircle2, Sparkles, ArrowRight, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';

/**
 * Reusable modal for clients to request Afterpay for a treatment.
 * The treatment and price are usually prefilled from the price tag they clicked.
 * Submits to POST /api/afterpay/request which notifies admin.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LOCATIONS = [
  { id: '3', label: 'Ottawa — Prince of Wales' },
  { id: '2', label: 'Kanata' },
  { id: '1', label: 'Carleton Place' },
];

export const AfterpayRequestModal = ({ open, onClose, treatment = '', price = '' }) => {
  const { language } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    treatment,
    price: typeof price === 'number' ? price : parseFloat(String(price).replace(/[^0-9.]/g, '')) || 0,
    location_id: '3',
    preferred_appointment_date: '',
    notes: '',
  });

  React.useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        treatment,
        price: typeof price === 'number' ? price : parseFloat(String(price).replace(/[^0-9.]/g, '')) || 0,
      }));
      setSuccess(false);
    }
  }, [open, treatment, price]);

  if (!open) return null;

  const t = (en, fr) => (language === 'fr' ? fr : en);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error(t('Please fill all required fields', 'Veuillez remplir les champs requis'));
      return;
    }
    if (!form.price || form.price < 1) {
      toast.error(t('Please enter a valid price', 'Veuillez entrer un prix valide'));
      return;
    }
    setSubmitting(true);
    try {
      // If the parent didn't pass a `treatment` prop (older call sites) and the
      // client left it blank, fall back to a price-anchored placeholder so
      // Karine's team still gets meaningful context in their inbox and the
      // submit isn't silently blocked by an HTML5 "please fill this field"
      // browser tooltip that mobile users often miss. Bug fix: Jul 3 2026 —
      // a client on the Fillers page couldn't proceed because the Treatment
      // field was `required` but never pre-filled by <AfterpayPriceTag/>.
      const payload = {
        ...form,
        treatment:
          (form.treatment || '').trim() ||
          t(`Afterpay request — $${Number(form.price).toFixed(0)} (treatment TBD)`,
            `Demande Afterpay — ${Number(form.price).toFixed(0)}$ (traitement à confirmer)`),
      };
      const r = await fetch(`${BACKEND}/api/afterpay/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      setSuccess(true);
      toast.success(t('Request submitted! Check your inbox for the invoice.', 'Demande envoyée! Vérifiez votre boîte courriel.'));
    } catch (err) {
      toast.error(t('Something went wrong. Please try again.', 'Une erreur est survenue.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end md:items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      data-testid="afterpay-request-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full md:max-w-xl rounded-t-3xl md:rounded-3xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up"
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-[#b2fce4] via-emerald-300 to-[#b2fce4] px-5 md:px-6 py-4 md:py-5 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 md:top-4 md:right-4 text-stone-900/70 hover:text-stone-900 transition-colors"
            data-testid="afterpay-modal-close"
          >
            <X className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-black flex items-center justify-center flex-shrink-0">
              <span className="text-[#b2fce4] font-black text-[10px] md:text-xs">after</span>
            </div>
            <div>
              <h3 className="text-base md:text-xl text-stone-900 font-semibold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('Pay with Afterpay', 'Payer avec Afterpay')}
              </h3>
              <p className="text-stone-900/80 text-xs md:text-sm mt-0.5">
                {t('4 interest-free payments — invoice sent via Square', '4 paiements sans intérêts — facture envoyée via Square')}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
              </div>
              <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('Request received!', 'Demande reçue!')}
              </h4>
              <p className="text-sm text-gray-600 max-w-sm mx-auto">
                {t(
                  "Karine's team will email a secure Square invoice to you within a few hours. Simply select Afterpay at checkout to pay in 4 installments.",
                  "L'équipe de Karine vous enverra une facture Square sécurisée. Sélectionnez Afterpay au paiement pour diviser en 4 paiements."
                )}
              </p>
              <Button onClick={onClose} className="mt-6 bg-stone-900 hover:bg-stone-800 text-white" data-testid="afterpay-modal-done">
                {t('Got it', 'Compris')}
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {/* Summary */}
              <div className="bg-gradient-to-r from-emerald-50 to-white rounded-xl p-3 md:p-4 border border-emerald-200 flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs md:text-sm text-gray-700 space-y-1">
                  <div className="font-semibold text-gray-900">{form.treatment || t('Custom Treatment', 'Traitement Personnalisé')}</div>
                  <div>
                    {t('Total', 'Total')}: <span className="font-bold">${Number(form.price).toFixed(2)}</span> + tax
                  </div>
                  <div className="text-xs text-gray-500">
                    {t('4 interest-free payments of', '4 paiements sans intérêts de')}{' '}
                    <span className="font-bold text-gray-800">${(Number(form.price) / 4).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">{t('First Name', 'Prénom')} *</label>
                  <input
                    required
                    data-testid="afterpay-input-firstname"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">{t('Last Name', 'Nom')} *</label>
                  <input
                    required
                    data-testid="afterpay-input-lastname"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Email + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">{t('Email', 'Courriel')} *</label>
                  <input
                    required type="email"
                    data-testid="afterpay-input-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">{t('Phone', 'Téléphone')} *</label>
                  <input
                    required
                    data-testid="afterpay-input-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Treatment / Price (editable in case prefill was missing) */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-gray-700 mb-1 block">
                    {t('Treatment', 'Traitement')}
                    <span className="ml-1 text-gray-400 font-normal">
                      {t('(optional — we\'ll confirm)', '(optionnel — nous confirmerons)')}
                    </span>
                  </label>
                  <input
                    data-testid="afterpay-input-treatment"
                    value={form.treatment}
                    placeholder={t('e.g. 1/2 Syringe Filler — Door Crasher', 'ex. 1/2 seringue de filler')}
                    onChange={(e) => setForm({ ...form, treatment: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">{t('Price (CAD)', 'Prix (CAD)')} *</label>
                  <input
                    required type="number" min="1" step="0.01"
                    data-testid="afterpay-input-price"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Location + Appointment */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {t('Location', 'Emplacement')}
                  </label>
                  <select
                    data-testid="afterpay-input-location"
                    value={form.location_id}
                    onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent bg-white"
                  >
                    {LOCATIONS.map((l) => (
                      <option key={l.id} value={l.id}>{l.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">
                    {t('Preferred Appointment Date (optional)', 'Date de RDV préférée (optionnel)')}
                  </label>
                  <input
                    type="text" placeholder={t('e.g. Apr 25 at 2pm', 'ex. 25 avril à 14h')}
                    data-testid="afterpay-input-appt"
                    value={form.preferred_appointment_date}
                    onChange={(e) => setForm({ ...form, preferred_appointment_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">{t('Notes (optional)', 'Notes (optionnel)')}</label>
                <textarea
                  rows={2}
                  data-testid="afterpay-input-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent resize-none"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={submitting}
                data-testid="afterpay-submit-btn"
                className="w-full bg-black hover:bg-stone-800 text-[#b2fce4] font-bold py-5 md:py-6 text-sm md:text-base"
              >
                {submitting ? (
                  <span>{t('Submitting…', 'Envoi en cours…')}</span>
                ) : (
                  <>
                    <Heart className="w-4 h-4 mr-2 fill-[#b2fce4]" />
                    {t('Request Afterpay Invoice', 'Demander la Facture Afterpay')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <p className="text-[10px] md:text-xs text-center text-gray-500 leading-relaxed">
                {t(
                  "You'll receive a secure Square invoice by email. Pay with Afterpay (4 interest-free payments) or credit card.",
                  "Vous recevrez une facture Square sécurisée par courriel. Payez avec Afterpay (4 paiements) ou carte."
                )}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AfterpayRequestModal;
